import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import { LoginFailure, loginApi } from "../services";
import type { AuthState, Credentials } from "../types";

const EMPTY: AuthState = { token: null, user: null };

const sessionStore = createJSONStorage<AuthState>(() => sessionStorage);

export const authAtom = atomWithStorage<AuthState>(
  "auth",
  EMPTY,
  sessionStore,
  {
    getOnInit: true,
  },
);

export const tokenAtom = atom((get) => get(authAtom).token);

export const currentUserAtom = atom((get) => get(authAtom).user);

export const isAuthenticatedAtom = atom((get) => get(authAtom).token !== null);

export const signingInAtom = atom(false);

export const signInErrorAtom = atom<string | null>(null);

export const signInAtom = atom(
  null,
  async (_get, set, credentials: Credentials) => {
    set(signingInAtom, true);
    set(signInErrorAtom, null);

    try {
      const result = await loginApi.login(credentials);

      set(authAtom, { token: result.token, user: result.user });

      return result.user;
    } catch (error) {
      set(
        signInErrorAtom,
        error instanceof LoginFailure || error instanceof Error
          ? error.message
          : "Login failed",
      );

      throw error;
    } finally {
      set(signingInAtom, false);
    }
  },
);

export const signOutAtom = atom(null, (_get, set) => {
  set(authAtom, EMPTY);
  set(signInErrorAtom, null);
});

export const authorizationHeaderAtom = atom((get): Record<string, string> => {
  const token = get(tokenAtom);

  return token ? { Authorization: `Bearer ${token}` } : {};
});
